/* ═══════════════════════════════════════════════════════════════
   Forma zone drag — what is in the air.

   `dataTransfer.getData` is sealed while a drag is over a target (browsers
   only hand it over on drop), so the slot targeting cannot read the payload
   to size the gap it opens. The drag SOURCES (palette chip, token on the
   sheet, token chip in the panel) tell this tiny module what they picked up
   instead: how wide it is, and which tokens to dim in place. A drag with no
   session (an unknown source) still works — the gap just uses a default.
   ═══════════════════════════════════════════════════════════════ */

export interface ZoneDragSession {
  /** Width of the dragged thing, in unscaled CSS px. */
  widthPx: number;
  /** Token keys being moved (their old place dims). Empty for a palette chip. */
  keys: string[];
}

let session: ZoneDragSession | null = null;

const DIM_CLASS = 'is-dragging-src';

/** Sum of the on-screen widths of these token keys (unscaled CSS px). */
export function measureTokenKeys(keys: string[]): number {
  let w = 0;
  for (const k of keys) {
    const el = document.querySelector<HTMLElement>(`[data-token-key="${CSS.escape(k)}"]`);
    if (el) w += el.offsetWidth || el.getBoundingClientRect().width;
  }
  return w;
}

export function endZoneDrag(): void {
  session = null;
  document.querySelectorAll('.' + DIM_CLASS).forEach((el) => el.classList.remove(DIM_CLASS));
  window.removeEventListener('dragend', endZoneDrag, true);
  window.removeEventListener('drop', endZoneDrag, true);
}

/** A source calls this from its `dragstart`. */
export function beginZoneDrag(s: ZoneDragSession): void {
  endZoneDrag();
  session = s;
  window.addEventListener('dragend', endZoneDrag, true);
  window.addEventListener('drop', endZoneDrag, true);
  if (s.keys.length) {
    // After the browser has taken its drag-image snapshot, so the image stays crisp.
    requestAnimationFrame(() => {
      if (session !== s) return;
      for (const k of s.keys) {
        document
          .querySelectorAll(`[data-token-key="${CSS.escape(k)}"]`)
          .forEach((el) => el.classList.add(DIM_CLASS));
      }
    });
  }
}

export function getZoneDrag(): ZoneDragSession | null {
  return session;
}
